"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import { component } from "@gnist/component-utils";
import "../../../building-blocks/inputs/internal/TextBelowInput.js";
import "../../../foundation/iconography/ExtraIconsProvider.js";
import "../../../foundation/iconography/IconVariantProvider.js";
import "../../../foundation/iconography/icons.js";
import "../../../foundation/iconography/Icon.js";
import "../../../foundation/typography/index.js";
import { forwardRef, useState, useEffect, useRef } from "react";
import "classnames";
import "../../../foundation/logos/Logo.css.js";
import "@gnist/themes/atoms.css.js";
import "../../../utilities/layout/Row.js";
import { tokens } from "@gnist/themes/tokens.css.js";
import "react-content-loader";
import { RectangularSkeleton } from "../../../building-blocks/skeletons/RectangularSkeleton.js";
import { useTranslation } from "../../../translations/index.js";
import { getStartOfDay, today, maxDayOfMonth, nextMonth, isSameDate, isNorwegianHoliday, minDayOfMonth, addDays } from "../../../utilities/time/date.js";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { makeBy, splitAt, chunksOf } from "fp-ts/Array";
import { pipe } from "fp-ts/function";
import "../../actions/buttons/PrimaryButton.js";
import "../../actions/buttons/SecondaryButton.js";
import "../../actions/buttons/SuccessButton.js";
import "../../actions/buttons/DangerButton.js";
import "../../actions/buttons/GhostButton.js";
import "../../actions/buttons/TextButton.js";
import { IconButton } from "../../actions/buttons/IconButton.js";
import "../../actions/selectionControls/Checkbox.js";
import "../../actions/selectionControls/RadioButton.js";
import "../../actions/chips/ActionChip.js";
import "../../actions/chips/FilterChip.js";
import "../../actions/chips/TagChip.js";
import { headerCell, containerStyle, selectMonthRow, buttonRow, arrowButton, gridStyle, rowStyle, firstDay, cellSkeletonStyle, cellRecipe } from "./Calendar.css.js";
const HeaderCell = component("HeaderCell", headerCell, "div");
function weekDayNumberStartingMonday(date) {
  return (date.getDay() + 6) % 7;
}
const Week = ({ weekDays, current, setCurrent, selected, setSelected, focused, setFocus, focusRef, isDateUnavailable, minDate, maxDate, isLoading }) => {
  function isDateWithinRange(date) {
    return date >= (minDate ?? -Infinity) && date <= (maxDate ?? Infinity);
  }
  const moveToDate = (date) => {
    if (!isDateWithinRange(date)) {
      return;
    }
    setCurrent(date);
    setFocus(date);
  };
  if (isLoading) {
    return jsx("div", { className: rowStyle, role: "row", style: {
      ...assignInlineVars({
        [firstDay]: weekDayNumberStartingMonday(weekDays[0]).toString()
      })
    }, children: weekDays.map((d) => jsx(RectangularSkeleton, { className: cellSkeletonStyle }, d.getTime())) });
  }
  return jsx("div", { className: rowStyle, role: "row", style: {
    ...assignInlineVars({
      [firstDay]: weekDayNumberStartingMonday(weekDays[0]).toString()
    })
  }, children: weekDays.map((d) => jsx("button", { className: cellRecipe({
    isToday: isSameDate(d, today()),
    isSelected: selected ? isSameDate(d, selected) : false,
    isHoliday: isNorwegianHoliday(d),
    isDisabled: !isDateWithinRange(d),
    isUnavailable: (isDateUnavailable == null ? void 0 : isDateUnavailable(d)) || false
  }), role: "gridcell", "aria-selected": selected ? isSameDate(d, selected) : false, disabled: !isDateWithinRange(d), "aria-disabled": (isDateUnavailable == null ? void 0 : isDateUnavailable(d)) ? true : void 0, tabIndex: isSameDate(current, d) ? 0 : -1, ref: isSameDate(focused, d) ? focusRef : void 0, onKeyDown: (event) => {
    switch (event.key) {
      case "ArrowUp":
        event.preventDefault();
        moveToDate(addDays(current, -7));
        break;
      case "ArrowDown":
        event.preventDefault();
        moveToDate(addDays(current, 7));
        break;
      case "ArrowLeft":
        event.preventDefault();
        moveToDate(addDays(current, -1));
        break;
      case "ArrowRight":
        event.preventDefault();
        moveToDate(addDays(current, 1));
        break;
      case "Home":
        event.preventDefault();
        if (event.ctrlKey) {
          if (minDate && minDayOfMonth(d) < minDate) {
            moveToDate(minDate);
            break;
          }
          moveToDate(minDayOfMonth(d));
          break;
        }
        if (minDate && weekDays[0] < minDate) {
          moveToDate(minDate);
          break;
        }
        moveToDate(weekDays[0]);
        break;
      case "End":
        event.preventDefault();
        if (event.ctrlKey) {
          if (maxDate && maxDayOfMonth(d) > maxDate) {
            moveToDate(maxDate);
            break;
          }
          moveToDate(maxDayOfMonth(d));
          break;
        }
        if (maxDate && weekDays[weekDays.length - 1] > maxDate) {
          moveToDate(maxDate);
          break;
        }
        moveToDate(weekDays[weekDays.length - 1]);
        break;
      case "PageUp":
        event.preventDefault();
        if (minDate && nextMonth(d, -1) < minDate) {
          moveToDate(minDate);
          break;
        }
        moveToDate(nextMonth(d, -1));
        break;
      case "PageDown":
        event.preventDefault();
        if (maxDate && nextMonth(d, 1) > maxDate) {
          moveToDate(maxDate);
          break;
        }
        moveToDate(nextMonth(d, 1));
        break;
    }
  }, onClick: () => {
    if (isDateUnavailable == null ? void 0 : isDateUnavailable(d)) {
      return;
    }
    moveToDate(d);
    setSelected(d);
  }, children: d.getDate() }, d.getTime())) });
};
function getWeeksInMonth(month) {
  const firstDayOfMonth = minDayOfMonth(month);
  const lastDayOfMonth = maxDayOfMonth(month);
  const numberOfDaysInFirstWeek = 7 - weekDayNumberStartingMonday(firstDayOfMonth);
  const numberOfDaysInMonth = lastDayOfMonth.getDate();
  const daysInMonth = makeBy(numberOfDaysInMonth, (n) => addDays(firstDayOfMonth, n));
  return pipe(daysInMonth, splitAt(numberOfDaysInFirstWeek), ([firstWeek, remainingDays]) => [
    firstWeek,
    ...chunksOf(7)(remainingDays)
  ]);
}
const DateGrid = ({ current, setCurrent, selected, setSelected, focused, setFocus, focusRef, isDateUnavailable, minDate, maxDate, isLoading }) => {
  const weeks = getWeeksInMonth(current);
  return jsx("div", { style: { display: "grid", gap: tokens.spacing.base }, role: "rowgroup", children: weeks.map((week) => jsx(Week, { weekDays: week, setCurrent, current, selected, setSelected, focused, setFocus, focusRef, isDateUnavailable, minDate, maxDate, isLoading }, week[0].toString())) });
};
function clampDate(date, minDate, maxDate) {
  if (minDate && date < minDate) {
    return minDate;
  }
  if (maxDate && date > maxDate) {
    return maxDate;
  }
  return date;
}
const Calendar = forwardRef(function Calendar2({ selected, setSelected, isDateUnavailable, minDate: origMinDate, maxDate: origMaxDate, onNavigate, isLoading }, ref) {
  const minDate = origMinDate ? getStartOfDay(origMinDate) : void 0;
  const maxDate = origMaxDate ? getStartOfDay(origMaxDate) : void 0;
  const text = useTranslation((t) => t.components.inputs.pickers.calendar);
  const days = text.shortDayNames;
  const getMonthName = (month) => {
    if (month <= 0 || month > 12) {
      return "";
    }
    return text.monthNames[month];
  };
  const [current, internalSetCurrent] = useState(clampDate(today(), minDate, maxDate));
  useEffect(() => {
    internalSetCurrent((d) => clampDate(d, minDate, maxDate));
  }, [maxDate, minDate]);
  const setCurrent = (date) => {
    internalSetCurrent(date);
    onNavigate == null ? void 0 : onNavigate(date);
  };
  const [focused, setFocus] = useState(current);
  const focusRef = useRef(null);
  useEffect(() => {
    var _a;
    (_a = focusRef.current) == null ? void 0 : _a.focus();
  }, [focused]);
  const handleChangeMonth = (count) => {
    const tmp = nextMonth(current, count);
    if (count === -1) {
      const nextDate = minDate && tmp < minDate ? minDate : tmp;
      setCurrent(nextDate);
    } else {
      const nextDate = maxDate && tmp > maxDate ? maxDate : tmp;
      setCurrent(nextDate);
    }
  };
  return jsxs("div", { className: containerStyle, role: "group", "aria-label": text.calendar, ref, children: [jsxs("div", { className: selectMonthRow, children: [jsxs("span", { "aria-live": "polite", children: [getMonthName(current.getMonth() + 1), " ", current.getFullYear(), " "] }), jsxs("div", { className: buttonRow, children: [jsx(IconButton, { className: arrowButton, icon: "arrow_backward", onClick: () => {
    handleChangeMonth(-1);
  }, label: text.showPreviousMonth, disabled: minDate && maxDayOfMonth(nextMonth(current, -1)) < minDate }), jsx(IconButton, { className: arrowButton, icon: "arrow_forward", onClick: () => {
    handleChangeMonth(1);
  }, label: text.showNextMonth, disabled: maxDate && nextMonth(current, 1) > maxDate })] })] }), jsxs("div", { className: gridStyle, role: "grid", children: [jsxs("div", { className: rowStyle, role: "row", style: { height: "unset" }, children: [jsx(HeaderCell, { role: "columnheader", children: days.mon }), jsx(HeaderCell, { role: "columnheader", children: days.tue }), jsx(HeaderCell, { role: "columnheader", children: days.wed }), jsx(HeaderCell, { role: "columnheader", children: days.thu }), jsx(HeaderCell, { role: "columnheader", children: days.fri }), jsx(HeaderCell, { role: "columnheader", children: days.sat }), jsx(HeaderCell, { role: "columnheader", children: days.sun })] }), jsx(DateGrid, { current, setCurrent, selected, setSelected, focused, setFocus, focusRef, isDateUnavailable, minDate, maxDate, isLoading: !!isLoading })] })] });
});
export {
  Calendar
};
